/**
 * Party table component for DEX admin dashboards.
 * Displays parties in a sortable table with inline expansion and two-step delete.
 * Used by both admin (writers/readers) and super-admin (admins/super-admins) dashboards.
 */

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Get sort icon for a column header.
 * @param {string} column - Column name
 * @param {string} sortColumn - Currently sorted column
 * @param {string} sortDirection - Current sort direction ('asc' or 'desc')
 * @returns {string} Sort icon HTML
 */
function sortIcon(column, sortColumn, sortDirection) {
  if (column !== sortColumn) {
    // Neutral arrows for non-active columns
    return `<span class="text-gray-400 ml-1">&#8693;</span>`;
  }
  // Active column - show direction
  return sortDirection === 'asc'
    ? `<span class="text-blue-600 ml-1">&#8593;</span>`
    : `<span class="text-blue-600 ml-1">&#8595;</span>`;
}

/**
 * Sort parties array by column and direction.
 * @param {Array} parties - Array of party objects
 * @param {string} column - Column to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted array (new copy)
 */
function sortParties(parties, column, direction) {
  return [...parties].sort((a, b) => {
    let aVal = a[column] || '';
    let bVal = b[column] || '';

    // String comparison
    const comparison = String(aVal).localeCompare(String(bVal));
    return direction === 'asc' ? comparison : -comparison;
  });
}

/**
 * Format a date string for display.
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date string
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

/**
 * Get display name for a role.
 * @param {string} role - Role identifier
 * @returns {string} Human-readable role name
 */
function getRoleDisplayName(role) {
  const names = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'writer': 'Writer',
    'reader': 'Reader'
  };
  return names[role] || role;
}

/**
 * Render the party table into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {Array} [state.parties=[]] - Array of party objects
 * @param {Object} [state.partyKeyCounts={}] - Key counts by party ID
 * @param {string} [state.sortColumn='party_id'] - Column to sort by
 * @param {string} [state.sortDirection='asc'] - Sort direction
 * @param {string|null} [state.expandedPartyId=null] - Currently expanded party ID
 * @param {string|null} [state.deleteConfirming=null] - Party ID in delete confirm state
 * @param {boolean} [state.loading=false] - Whether data is loading
 * @param {string|null} [state.currentPartyId=null] - Current session's party ID for self-management
 * @param {boolean} [state.isLastSuperAdmin=false] - Whether the table shows only one super-admin
 */
export function renderPartyTable(container, state = {}) {
  const {
    parties = [],
    partyKeyCounts = {},
    sortColumn = 'party_id',
    sortDirection = 'asc',
    expandedPartyId = null,
    deleteConfirming = null,
    loading = false,
    currentPartyId = null,
    isLastSuperAdmin = false
  } = state;

  // Loading state - skeleton rows
  if (loading) {
    container.innerHTML = `
      <div class="border border-gray-200 rounded-lg overflow-hidden">
        <div class="animate-pulse">
          <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div class="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div class="divide-y divide-gray-200">
            ${[1, 2, 3].map(() => `
              <div class="px-4 py-3 flex gap-4">
                <div class="h-4 bg-gray-200 rounded flex-1"></div>
                <div class="h-4 bg-gray-200 rounded w-16"></div>
                <div class="h-4 bg-gray-200 rounded w-24"></div>
                <div class="h-4 bg-gray-200 rounded w-12"></div>
                <div class="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;
    return;
  }

  // Empty state
  if (parties.length === 0) {
    container.innerHTML = `
      <div class="border border-gray-200 rounded-lg p-8 text-center">
        <p class="text-gray-500 text-sm">
          No parties found.
        </p>
      </div>
    `;
    return;
  }

  // Sort parties
  const sorted = sortParties(parties, sortColumn, sortDirection);

  // Build table rows
  const rows = sorted.map(party => {
    const isExpanded = party.party_id === expandedPartyId;
    const isConfirming = party.party_id === deleteConfirming;
    const keyCount = partyKeyCounts[party.party_id];
    const keyCountDisplay = keyCount !== undefined ? keyCount : '-';

    // Self-management checks
    const isSelf = currentPartyId && party.party_id === currentPartyId;
    const isSuperAdmin = party.role === 'super_admin';
    const cannotDelete = isSelf || (isSuperAdmin && isLastSuperAdmin);

    // Determine delete button title
    let deleteTitle = 'Delete this party';
    if (isSelf) {
      deleteTitle = 'Cannot delete yourself';
    } else if (isSuperAdmin && isLastSuperAdmin) {
      deleteTitle = 'Cannot delete the last super admin';
    }

    // Action buttons - two-step delete
    let actionsHtml;
    if (isConfirming) {
      actionsHtml = `
        <span class="text-sm text-gray-600 mr-2">Delete?</span>
        <button
          type="button"
          class="text-red-600 hover:underline text-sm mr-2 font-medium"
          data-action="confirm-delete"
          data-party-id="${escapeHtml(party.party_id)}"
        >Confirm</button>
        <button
          type="button"
          class="text-gray-600 hover:underline text-sm"
          data-action="cancel-delete"
          data-party-id="${escapeHtml(party.party_id)}"
        >Cancel</button>
      `;
    } else {
      // Delete button: disabled if self or last super-admin
      const deleteButtonClasses = cannotDelete
        ? 'text-gray-400 cursor-not-allowed text-sm'
        : 'text-red-600 hover:underline text-sm';

      actionsHtml = `
        <button
          type="button"
          class="text-blue-600 hover:underline text-sm mr-3"
          data-action="expand"
          data-party-id="${escapeHtml(party.party_id)}"
        >${isExpanded ? 'Collapse' : 'View'}</button>
        <button
          type="button"
          class="${deleteButtonClasses}"
          data-action="delete"
          data-party-id="${escapeHtml(party.party_id)}"
          ${cannotDelete ? 'disabled' : ''}
          title="${deleteTitle}"
        >Delete</button>
      `;
    }

    // Main row
    const rowHtml = `
      <tr class="hover:bg-gray-50 ${isExpanded ? 'bg-blue-50' : ''}">
        <td class="px-4 py-3 font-mono text-xs truncate max-w-xs" title="${escapeHtml(party.party_id)}">
          ${escapeHtml(party.party_id)}
        </td>
        <td class="px-4 py-3">
          <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-100">
            <span class="w-2 h-2 rounded-full role-dot-${escapeHtml(party.role)}"></span>
            <span class="text-xs font-medium role-text-${escapeHtml(party.role)}">
              ${escapeHtml(getRoleDisplayName(party.role))}
            </span>
          </span>
        </td>
        <td class="px-4 py-3 text-sm text-gray-600">
          ${escapeHtml(formatDate(party.created_at))}
        </td>
        <td class="px-4 py-3 text-sm text-gray-600 text-center">
          ${keyCountDisplay}
        </td>
        <td class="px-4 py-3">
          ${actionsHtml}
        </td>
      </tr>
    `;

    // Expansion placeholder row (for details component to render into)
    const expansionHtml = isExpanded ? `
      <tr>
        <td colspan="5" class="p-0">
          <div id="party-details-${escapeHtml(party.party_id)}" class="bg-gray-50 border-t border-gray-200"></div>
        </td>
      </tr>
    ` : '';

    return rowHtml + expansionHtml;
  }).join('');

  // Render table
  container.innerHTML = `
    <div class="border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm text-left">
        <thead class="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th
              class="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
              data-sort="party_id"
            >
              Party ID ${sortIcon('party_id', sortColumn, sortDirection)}
            </th>
            <th
              class="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
              data-sort="role"
            >
              Role ${sortIcon('role', sortColumn, sortDirection)}
            </th>
            <th
              class="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
              data-sort="created_at"
            >
              Created ${sortIcon('created_at', sortColumn, sortDirection)}
            </th>
            <th class="px-4 py-3 text-center">
              # Keys
            </th>
            <th class="px-4 py-3">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          ${rows}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Initialize the party table with event handlers.
 * Uses delegated event handling for sort and action buttons.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onSort] - Called when column header clicked: (column) => void
 * @param {Function} [callbacks.onExpand] - Called when expand/view clicked: (partyId) => void
 * @param {Function} [callbacks.onDelete] - Called when delete clicked: (partyId) => void
 * @param {Function} [callbacks.onConfirmDelete] - Called when confirm delete clicked: (partyId) => void
 * @param {Function} [callbacks.onCancelDelete] - Called when cancel delete clicked: (partyId) => void
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initPartyTable(container, callbacks = {}) {
  const { onSort, onExpand, onDelete, onConfirmDelete, onCancelDelete } = callbacks;

  // Delegated click handler for the entire container
  function handleClick(e) {
    // Check for sort header click
    const sortHeader = e.target.closest('[data-sort]');
    if (sortHeader && onSort) {
      onSort(sortHeader.dataset.sort);
      return;
    }

    // Check for action button click
    const actionButton = e.target.closest('[data-action]');
    if (actionButton) {
      // Ignore if button is disabled
      if (actionButton.disabled) return;

      const action = actionButton.dataset.action;
      const partyId = actionButton.dataset.partyId;

      if (!partyId) return;

      switch (action) {
        case 'expand':
          if (onExpand) onExpand(partyId);
          break;
        case 'delete':
          if (onDelete) onDelete(partyId);
          break;
        case 'confirm-delete':
          if (onConfirmDelete) onConfirmDelete(partyId);
          break;
        case 'cancel-delete':
          if (onCancelDelete) onCancelDelete(partyId);
          break;
      }
    }
  }

  container.addEventListener('click', handleClick);

  // Return cleanup function
  return function cleanup() {
    container.removeEventListener('click', handleClick);
  };
}
